import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Loader2, MapPinOff, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { type PulseResponse } from "./pulse-types";
import { MissedJobsSection, type MissedJobActionHandlers, type MissedJobsResponse } from "./missed-jobs-section";
import type { DispatchTech } from "./dispatch-types";
import { LeftOpenList, type LeftOpenListResponse } from "@/components/left-open/left-open-list";

// ─── TriageAlertBar ────────────────────────────────────────────────────
//
// Declutter wrapper that collapses the live-pulse triage + missed-jobs
// triage into a single slim bar showing just the headline counts (e.g.
// "3 at risk · 2 overdue · 1 missed"). Clicking the bar (or the command
// bar's Attention counter) expands it.
//
// #761 (one scoreboard): the expanded state used to re-render two more
// scoreboards — DispatchStatStrip and the OpsPulseStrip headline chips —
// duplicating the command-bar counters in different words. Both are
// retired. The expanded state now shows what NO counter can: the per-job
// list of at-risk/overdue work with the server's plain-English reason for
// each ("On site 96m vs planned 60m", "Past scheduled end without start"),
// then the missed-jobs triage.

interface TriageAlertBarProps {
    mode: "day" | "week";
    pulse: PulseResponse | undefined;
    pulseLoading: boolean;
    pulseFetching: boolean;
    onPulseRefresh: () => void;
    missed: MissedJobsResponse | undefined;
    techs: DispatchTech[];
    missedActions: MissedJobActionHandlers;
    missedSectionId?: string;
    /** Scheduled jobs today with no map coordinates (can't render on the
     *  map or route planner) — surfaced as a data-quality line, not a pill. */
    missingCoordsCount?: number;
    /** G6.1: expansion is controlled by the shell so the command bar's
     *  attention counter can open the pulse detail from outside. */
    expanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    /** AUTOMATION PRIVACY (§8): the missed-jobs Auto-fill button is an
     *  automation affordance — render it only for dispatch.manage holders. */
    showMissedAutoFill: boolean;
    /** id on the wrapper so the command bar can scroll it into view. */
    id?: string;
    /** No job left open: with the switch on for this user, the Left open list
     *  replaces Missed jobs (same place, same count slot). */
    leftOpen?: LeftOpenListResponse;
}

export function TriageAlertBar({
    mode,
    pulse,
    pulseLoading,
    pulseFetching,
    onPulseRefresh,
    missed,
    techs,
    missedActions,
    missedSectionId,
    missingCoordsCount = 0,
    expanded,
    onExpandedChange,
    showMissedAutoFill,
    id,
    leftOpen,
}: TriageAlertBarProps) {
    const leftOpenOn = leftOpen?.switchOn === true;
    const atRisk = pulse?.counts.atRisk ?? 0;
    const overdue = pulse?.counts.overdue ?? 0;
    const missedCount = leftOpenOn ? (leftOpen?.counts.total ?? 0) : (missed?.jobs.length ?? 0);
    const showPulse = mode === "day";

    // In week mode there's no pulse, so the bar is only meaningful when there
    // are missed jobs to triage.
    if (!showPulse && missedCount === 0) return null;

    const trouble = atRisk + overdue + missedCount;

    const parts: string[] = [];
    if (atRisk > 0) parts.push(`${atRisk} at risk`);
    if (overdue > 0) parts.push(`${overdue} overdue`);
    if (missedCount > 0) parts.push(`${missedCount} ${leftOpenOn ? "left open" : "missed"}`);
    const summary = parts.length > 0 ? parts.join(" · ") : "All clear";

    const attentionJobs = showPulse
        ? (pulse?.pulses ?? []).filter((p) => p.risk === "at_risk" || p.risk === "overdue")
        : [];

    return (
        <div
            id={id}
            className={cn(
                "rounded-lg border transition-colors backdrop-blur-sm",
                trouble > 0
                    ? "border-amber-300 bg-amber-50/95 dark:border-amber-800/70 dark:bg-amber-950/80"
                    : "border-emerald-300/60 bg-emerald-50/95 dark:border-emerald-800/50 dark:bg-emerald-950/80",
            )}
        >
            <button
                type="button"
                onClick={() => onExpandedChange(!expanded)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left"
                aria-expanded={expanded}
            >
                {trouble > 0 ? (
                    <AlertTriangle
                        className={cn(
                            "h-4 w-4 shrink-0",
                            overdue > 0 ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400",
                        )}
                    />
                ) : (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                )}
                <span className="text-sm font-semibold">{summary}</span>
                {pulseFetching && !pulseLoading && <span className="text-[11px] text-foreground/60">updating…</span>}
                <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-foreground/70">
                    {expanded ? "Hide details" : "Show details"}
                    {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </span>
            </button>

            {expanded && (
                <div className="space-y-3 border-t border-border/60 px-3 py-3">
                    {showPulse && (
                        <div className="rounded-lg border bg-card px-3 py-2.5">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span className="text-sm font-semibold">Needs a human</span>
                                <span className="text-[11px] text-foreground/70">
                                    At risk = running long or a late start · Overdue = past its scheduled end, never
                                    started · Missed = never ran on its day, needs a new slot
                                </span>
                                <span className="ml-auto flex items-center gap-2 text-[11px] text-foreground/80">
                                    {pulseLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                                    {pulse?.now && (
                                        <span>
                                            as of{" "}
                                            {new Date(pulse.now).toLocaleTimeString(undefined, {
                                                hour: "numeric",
                                                minute: "2-digit",
                                            })}
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={onPulseRefresh}
                                        className="inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-muted"
                                        title="Refresh"
                                    >
                                        <RefreshCw className={cn("h-3 w-3", pulseFetching && "animate-spin")} />
                                    </button>
                                </span>
                            </div>
                            {attentionJobs.length === 0 ? (
                                <p className="mt-1.5 text-sm text-foreground/70">
                                    No jobs are running long or overdue right now.
                                </p>
                            ) : (
                                <ul className="mt-1.5 divide-y divide-border/60">
                                    {attentionJobs.map((p) => (
                                        <li key={p.jobId}>
                                            <Link
                                                to={`/jobs/${p.jobId}`}
                                                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded px-1 py-1.5 -mx-1 hover:bg-muted"
                                            >
                                                <AlertTriangle
                                                    className={cn(
                                                        "h-3.5 w-3.5 shrink-0 self-center",
                                                        p.risk === "overdue"
                                                            ? "text-red-600 dark:text-red-400"
                                                            : "text-amber-600 dark:text-amber-400",
                                                    )}
                                                />
                                                <span className="text-sm font-medium">{p.title}</span>
                                                {p.techName && (
                                                    <span className="text-xs text-foreground/70">{p.techName}</span>
                                                )}
                                                <span className="text-xs text-foreground/80">{p.note}</span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {missingCoordsCount > 0 && (
                                <p className="mt-2 flex items-center gap-1.5 border-t border-border/60 pt-2 text-xs text-foreground/70">
                                    <MapPinOff className="h-3.5 w-3.5 shrink-0" />
                                    {missingCoordsCount} scheduled job{missingCoordsCount === 1 ? "" : "s"} today{" "}
                                    {missingCoordsCount === 1 ? "has" : "have"} no map pin — they can't appear on the
                                    map or route planner.
                                </p>
                            )}
                        </div>
                    )}
                    {leftOpenOn && leftOpen ? (
                        <LeftOpenList data={leftOpen} />
                    ) : (
                        <MissedJobsSection
                            response={missed}
                            techs={techs}
                            onAction={missedActions}
                            sectionId={missedSectionId}
                            showAutoFill={showMissedAutoFill}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
